import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { auth } from "../services/firebase";
import {
  ArrowLeftIcon,
  AtSignIcon,
  CheckIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";

import { crashlytics } from "../services/firebase";
const ForgotPasswordPage: React.FC = () => {
  usePageTracking("ForgotPassword", "ForgotPasswordPage");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // First, check what sign-in methods are available for this email
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);

      // Check if the user signed up with Google (google.com provider)
      if (signInMethods.length > 0 && !signInMethods.includes("password")) {
        // User has an account but it's not using password authentication
        const hasGoogleProvider = signInMethods.includes("google.com");

        if (hasGoogleProvider) {
          setError(
            'This account was created with Google Sign-In. Please use the "Continue with Google" button to sign in.',
          );
          setIsLoading(false);
          return;
        } else {
          // Other OAuth providers (if any are added in the future)
          setError(
            "This account uses a different sign-in method. Please use the appropriate sign-in option.",
          );
          setIsLoading(false);
          return;
        }
      }

      // If we get here, user either has password auth or no account exists
      // Firebase will handle the "no account" case with an error
      await sendPasswordResetEmail(auth, email);
      setEmailSent(true);
    } catch (err) {
      crashlytics.logError(
        `Error sending password reset email: ${JSON.stringify(err?.message)}`,
        "ForgotPasswordPage.tsx",
        err,
      );
      if (err instanceof Error) {
        if (err.message.includes("auth/user-not-found")) {
          setError("No account found with this email address.");
        } else if (err.message.includes("auth/invalid-email")) {
          setError("Please enter a valid email address.");
        } else if (err.message.includes("auth/network-request-failed")) {
          setError("Network error. Please check your internet connection.");
        } else if (err.message.includes("auth/too-many-requests")) {
          setError("Too many requests. Please try again later.");
        } else {
          setError(`Failed to send reset email: ${err.message}`);
        }
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="h-full bg-white text-gray-800 flex flex-col p-6">
        <header className="w-full max-w-sm mx-auto">
          <button
            onClick={() => navigate("/login")}
            className="text-gray-400 hover:text-gray-700 p-2 -ml-2"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
        </header>

        <main className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-sm mx-auto text-center animate-fade-in-zoom">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckIcon className="w-8 h-8 text-green-600" />
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-3">
              Check Your Email
            </h1>

            <p className="text-gray-600 mb-2">
              We've sent a password reset link to:
            </p>
            <p className="font-semibold text-gray-900 mb-6">{email}</p>

            <p className="text-sm text-gray-500 mb-8">
              Click the link in the email to reset your password. If you don't
              see it, check your spam folder.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-violet-600 text-white font-bold py-3.5 px-4 rounded-full hover:bg-violet-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-violet-500/30"
              >
                Back to Sign In
              </button>

              <button
                onClick={() => {
                  setEmailSent(false);
                  setEmail("");
                }}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3.5 px-4 rounded-full hover:bg-gray-50 transition-all duration-300"
              >
                Try Another Email
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-full bg-white text-gray-800 flex flex-col p-6">
      <header className="w-full max-w-sm mx-auto">
        <button
          onClick={() => navigate("/login")}
          className="text-gray-400 hover:text-gray-700 p-2 -ml-2"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
      </header>

      <main className="flex-1 flex flex-col justify-center items-center">
        <div className="w-full max-w-sm mx-auto animate-fade-in-zoom">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Reset Password
            </h1>
            <p className="text-gray-500 mt-2 leading-relaxed">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <AtSignIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full bg-gray-100 border-gray-200 border rounded-full pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-800 placeholder-gray-500"
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center font-semibold">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="w-full bg-violet-600 text-white font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-violet-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-violet-500/30"
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              Remember your password?{" "}
              <Link
                to="/login"
                className="font-bold text-violet-600 hover:underline"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
